/**
 * Running a date between two seats (in the sim: no screen, no renderer; the game runs it, and so can a server). The flow is the same whoever sits in them; each seat has a DECIDER that makes
 * that person's choices:
 *  - LocalDecider: you, on this screen (the modals);
 *  - NpcDecider: an NPC, from the sim (npcAnswer, npcBill, npcAccepts: from how they feel);
 *  - a remote player (multiplayer): the same interface over the network, added with the backend. Nothing in the
 *    flow below changes when it arrives.
 * The flow: both arrive (first impressions), order, three topics (they raise two, you raise one: answering is the
 * other seat's job), the bill (each says what they'd do), the goodbye (one asks, the other answers).
 */
import { answered, dateTopics, firstImpression, impressed, npcAccepts, npcAnswer, npcBill, otherSeat, settleBill, type AfterRequest, type BillChoice, type DateEnding, type DateSession } from './dating.ts';
import type { LifeState } from './life.ts';

export type Topic = { line: string; answers: [string, number, string, string?][] };
export interface Decider {
  /**
   * What makes these choices: 'npc' (the sim, from how they feel), 'local' (the person at this screen), 'remote'
   * (a real player over the network). A player's seat is NEVER driven by 'npc': the game designs consequences,
   * never a real person's decisions (checkSeats enforces it).
   */
  readonly drives: 'npc' | 'local' | 'remote';
  /** order for this person (a price for each course, 0 = skip) */
  order(menu: { label: string; price: number; kind: 'starter' | 'main' | 'drink' }[]): Promise<number>;
  /** raise something to talk about (pick from these) */
  raise(topics: Topic[]): Promise<Topic>;
  /** answer what the other person raised: the index of the answer */
  answer(t: Topic, from: string): Promise<number>;
  bill(total: number, withName: string): Promise<BillChoice>;
  /** what to ask for at the end */
  ending(withName: string, partnerAlready: boolean): Promise<DateEnding>;
  /** the other person asked for this: yes or no */
  respond(ending: DateEnding | AfterRequest, from: string): Promise<boolean>;
}

/** What the screen shows along the way (captions, the spark meter: how the OTHER person is feeling about you). */
export interface DateView {
  say(text: string, ms?: number): Promise<void>;
  spark(feel: number, note: string): void;
}

/** An NPC's choices, from the sim. */
export class NpcDecider implements Decider {
  readonly drives = 'npc' as const;
  private sess: DateSession; private seat: number; private country: string; private closeness: () => number;
  constructor(sess: DateSession, seat: number, country: string, closeness: () => number) { this.sess = sess; this.seat = seat; this.country = country; this.closeness = closeness; }
  async order(menu: { label: string; price: number; kind: string }[]) {
    const pick = (k: string) => menu.filter((m) => m.kind === k)[this.seat % 2]?.price ?? 0;
    return pick('drink') + pick('main') + (this.sess.seats[this.seat].feel > 0.5 ? pick('starter') : 0);
  }
  async raise(topics: Topic[]) { return topics[0]; }
  async answer(t: Topic) { return npcAnswer(this.sess, this.seat, t.answers); }
  async bill() { return npcBill(this.sess, this.seat, this.country); }
  async ending() { return (this.sess.seats[this.seat].feel > 0.7 ? 'again' : 'hug') as DateEnding; }
  /** how many times they've been asked something: asking again later in the evening is a fresh answer (it was the same roll every time) */
  private asked = 0;
  async respond(ending: DateEnding | AfterRequest) { return npcAccepts(this.sess, this.seat, ending, this.closeness(), this.sess.log.length * 131 + this.sess.plan.at + 7919 * this.asked++); }
}

/**
 * The rule, enforced: an NPC seat is decided by the sim; a seat with a person in it ('you' or 'player') only by that
 * person (their screen here, or theirs over the network). Throws rather than ever falling back to the sim.
 */
export function checkSeats(sess: DateSession, deciders: Decider[]) {
  sess.seats.forEach((seat, i) => {
    const d = deciders[i];
    if (seat.kind === 'npc' && d.drives !== 'npc') throw new Error(`seat ${i} (${seat.name}) is an NPC but is driven by ${d.drives}`);
    if (seat.kind !== 'npc' && d.drives === 'npc') throw new Error(`seat ${i} (${seat.name}) is a person: the sim must not decide for them`);
  });
}

export interface DateResult { ending: DateEnding; accepted: boolean; total: number; share: [number, number]; asker: number }

/**
 * Run the evening. `me` is the seat on this screen (for what's shown); `deciders[i]` makes seat i's choices.
 * Returns how it ended; the caller applies it to each life (finishDate, payShare) on its own side.
 */
export async function runDateSession(s: LifeState, sess: DateSession, me: number, deciders: [Decider, Decider], view: DateView, lateMin: [number, number],
  menu: { label: string; price: number; kind: 'starter' | 'main' | 'drink' }[], country: string,
  /** the other seat's screen, when it's a person somewhere else: the same evening told from their side */
  mirror?: DateView): Promise<DateResult> {
  checkSeats(sess, deciders);
  const them = otherSeat(me);
  const name = (i: number) => sess.seats[i].name.split(' ')[0];
  const show = (note: string, theirs = note) => { view.spark(sess.seats[them].feel, note); mirror?.spark(sess.seats[me].feel, theirs); };
  // first impressions: each walked in somehow, and the other noticed (your own arrival is what this life knows)
  const fi = firstImpression(s, lateMin[me]);
  impressed(sess, me, fi.impression);
  impressed(sess, them, 0.65); // (the other person's arrival is theirs to account for; an NPC is always presentable)
  show(fi.notes.join(' '));
  void mirror?.say(`"Hi." ${name(me)} is here.`, 2600);
  await view.say(lateMin[me] > 20 ? `"There you are." ${name(them)} has finished a drink already.` : `"Hi." ${name(them)} smiles. "You look nice."`, 2600);
  // order: each person orders for themselves
  const spent = await Promise.all(deciders.map((d) => d.order(menu)));
  const total = spent[0] + spent[1];
  // talk: they raise two, you raise one (the other always answers)
  const pool = dateTopics(s).sort(() => Math.random() - 0.5);
  const turns = [them, me, them];
  for (const [k, asker] of turns.entries()) {
    const answerer = otherSeat(asker);
    void k;
    const t = await deciders[asker].raise(pool.slice(0, asker === me ? 3 : 1));
    pool.splice(pool.indexOf(t), 1);
    if (asker === me) await view.say(`You ask: ${t.line}`, 1800);
    else void mirror?.say(`You ask: ${t.line}`, 1800);
    const i = await deciders[answerer].answer(t, name(asker));
    const [said, value, why, theySay] = t.answers[i];
    answered(sess, asker, value);
    // you answered them: how it landed; they answered you: what they said
    const line = asker === them ? why : theySay ?? `${name(them)}: ${said}`;
    const theirs = asker === them ? theySay ?? `${name(me)}: ${said}` : why; // (the same turn, from the other side)
    show(line, theirs);
    void mirror?.say(theirs, 2200);
    await view.say(line, 2200);
  }
  // the bill: each says what they'd do
  const choice = await Promise.all(deciders.map((d, i) => d.bill(total, sess.seats[otherSeat(i)].name))) as [BillChoice, BillChoice];
  const b = settleBill(sess, choice, country);
  show(b.line);
  void mirror?.say(b.line, 2400);
  await view.say(b.line, 2400);
  // the goodbye: you ask (on this screen it's your call), they answer
  const partnerAlready = ['Girlfriend', 'Boyfriend', 'Wife', 'Husband', 'Partner'].includes(s.contacts.find((c) => c.name === sess.seats[them].name)?.relation ?? '');
  const ending = await deciders[me].ending(sess.seats[them].name, partnerAlready);
  const accepted = await deciders[them].respond(ending, sess.seats[me].name);
  return { ending, accepted, total, share: b.share, asker: me };
}
